import React, { ReactElement, useCallback, useEffect, useState, } from 'react';
import {
    RiskMinor
} from '@shopify/polaris-icons';
import { Tabs, Icon, BlockStack, Text, InlineStack, Modal, Thumbnail, Badge, Box, Link, InlineGrid, SkeletonDisplayText, SkeletonBodyText } from '@shopify/polaris';
import { DI, DIProps } from '../../../../../Core';
import { useTranslation } from 'react-i18next';
import { renderActivityMsg } from '../../../Listing Grid/Components/RenderErrorModal';
import { ErrorMessage } from './ProductErrorsN';

interface Props extends DIProps {
    from: string;
    type: string;
    id: string;
    errorCount: any;
    viewProductWarning: boolean;
    setViewProductWarning: React.Dispatch<React.SetStateAction<boolean>>;
}

export const capitalizeFirstLetter = (str: string): string => {
    // Check if the input is not an empty string
    if (str && typeof str === 'string') {
        // Capitalize the first letter and concatenate the rest of the string
        return str.charAt(0).toUpperCase() + str.slice(1);
    } else {
        // Return an empty string or the input if it's not a valid string
        return str || '';
    }
}

function FeedActivity({ data }: any): ReactElement {
    return <>
        {data && Object?.keys(data)?.map((activity: any, counter: number) => {
            return (
                <Box key={counter} padding="500">
                    <InlineGrid gap="400" columns='2'>
                        <BlockStack gap="100">
                            <Text as='p' fontWeight="semibold">{renderActivityMsg(data?.[activity]?.['msg'])}</Text>
                            <Text as='p' tone='subdued'>{data?.[activity]?.['time']}</Text>
                        </BlockStack>
                        <Text as='p' tone='subdued'><em>{data?.[activity]?.['msg']}</em></Text>
                    </InlineGrid>
                </Box>
            );
        })}
    </>
}

function ProductWarningsN(props: Props): ReactElement {
    const { t } = useTranslation();
    const [loader, setLoader] = useState<boolean>(true);
    const [warnings, setWarnings] = useState<any>({ parent: {}, variants: {} });
    const { viewProductWarning, setViewProductWarning } = props;
    const [tabs, setTabs] = useState<any>([]);
    const [processTags, setProcessTags] = useState<any>({});

    const [selectedTab, setSelectedTab] = useState(0);
    const handleTabChange = useCallback((selectedTabIndex: number) => {
        setSelectedTab(selectedTabIndex)
    }, []);

    const groupByType = (arr: any) => {
        return arr?.reduce((acc: any, obj: any) => {
            const key = obj.type;
            if (!acc[key]) {
                acc[key] = []; // Initialize the array if key doesn't exist
            }
            acc[key].push(obj);
            return acc;
        }, {});
    };

    useEffect(() => {
        if (viewProductWarning) {
            let payload = {};
            if (props.from == "grid") {
                if (props.type == "parent") {
                    payload = {
                        isParent: true,
                        container_id: props?.id ?? ''
                    }
                }
                else {
                    payload = {
                        isParent: false,
                        source_product_id: props?.id ?? ''
                    }
                }
            }
            else {
                if (props?.match?.c_id) {
                    payload = {
                        isParent: true,
                        container_id: props?.match?.c_id ?? props?.location?.state?.status?.container_id ?? ''
                    }
                }
                if (props?.match?.c_id && props?.match?.s_id) {
                    payload = {
                        isParent: false,
                        source_product_id: props?.match?.s_id ?? props?.location?.state?.source_product_id ?? ''
                    }
                }
            }
            props.di.POST("connector/product/getErrorProcessTag", payload).then((res: any) => {
                setLoader(false);
                if (res?.success) {
                    let tabs = [];
                    let tempProcessTags = { child: [], parent: [] };
                    if (res?.data?.parent && res?.data?.parent?.process_tags && Object.keys(res?.data?.parent?.process_tags)?.length > 0) {
                        tempProcessTags['parent'] = res?.data?.parent?.process_tags;
                    }
                    if (res?.data?.child && res?.data?.child?.process_tags && Object.keys(res?.data?.child?.process_tags)?.length > 0) {
                        tempProcessTags['child'] = res?.data?.child?.process_tags;
                    }
                    setProcessTags(tempProcessTags);
                    if (res?.data?.parent && res?.data?.parent?.warning && res?.data?.parent?.warning?.length > 0) {
                        tabs.push({
                            id: 'product-warnings',
                            content: t('Product Warnings'),
                            badge: res?.data?.parent?.warning ? res?.data?.parent?.warning?.length : 0,
                            accessibilityLabel: 'Product Warnings',
                            panelID: 'product-orders-content-1',
                        });
                    }
                    if (res?.data?.child && res?.data?.child?.warning && res?.data?.child?.warning?.length > 0) {
                        tabs.push({
                            id: 'variant-warnings',
                            content: t('Variant Warnings'),
                            badge: res?.data?.child.warning
                                ? res?.data?.child?.warning?.reduce((acc: any, warning: any) => acc + (warning.count || 0), 0)
                                : 0,
                            accessibilityLabel: 'Variant Warnings',
                            panelID: 'variant-orders-content-1',
                        });
                    }
                    setTabs(tabs);

                    // Transforming backend data into the desired format
                    const transformedData = {
                        parent: {
                            title: res?.data?.parent?.title,
                            image: res?.data?.parent?.image,
                            warnings: groupByType(res?.data?.parent?.warning),
                        },
                        // Check if the child data is nested or in the same schema as parent
                        variants: res?.data?.child?.title
                            ? {
                                title: res?.data?.child?.title,
                                image: res?.data?.child?.image,
                                warnings: groupByType(res?.data?.child?.warning),
                            }
                            : groupByType(res?.data?.child?.warning),
                    };
                    setWarnings(transformedData);
                }
            })
        }
    }, [viewProductWarning])

    const renderProductErrorTab = () => {
        return (
            <BlockStack gap={"300"}>
                <InlineStack blockAlign='center' gap={"300"}>
                    <Thumbnail
                        source={warnings?.parent?.image}
                        alt="Product image"
                        size="small"
                    />
                    <Text as={'p'} variant='bodyMd' fontWeight='medium'>{warnings?.parent?.title}</Text>
                </InlineStack>
                <ErrorTabs warnings={warnings?.parent?.warnings} />
            </BlockStack>
        );
    };

    const renderVariantErrorTab = () => {
        return (
            <BlockStack gap={"300"}>
                {/* Render variant-specific image and title if available */}
                {warnings.variants?.title && warnings.variants?.image && (
                    <InlineStack blockAlign='center' gap={"300"}>
                        <Thumbnail
                            source={warnings.variants.image}
                            alt="Variant image"
                            size="small"
                        />
                        <Text as={'p'} variant='bodyMd' fontWeight='medium'>{warnings.variants.title}</Text>
                    </InlineStack>
                )}
                {/* Conditionally render ErrorTabs based on the presence of the 'warnings' key in variants */}
                {warnings.variants.warnings ? (
                    <ErrorTabs warnings={warnings.variants.warnings} isVariant={true} />
                ) : (
                    <ErrorTabs warnings={warnings.variants} isVariant={true} />
                )}
            </BlockStack>
        );
    };

    function ErrorTabs({ warnings, isVariant = false }: any) {
        return warnings && Object.keys(warnings).map((type) => (
            <BlockStack key={type} gap={"200"}>
                <Text as={'p'} variant='bodyMd' fontWeight='medium'>{capitalizeFirstLetter(type)} {t("Warning(s)")}</Text>
                <BlockStack gap={"300"}>
                    {warnings[type].map((warning: any, index: any) => (
                        <InlineStack key={index} gap={"300"} blockAlign='start' wrap={false}>
                            <span className='min-w-20'><Icon source={RiskMinor} tone='critical' /></span>
                            <InlineStack gap={{ sm: "100", xs: "100", md: "300", lg: "300", xl: "300" }}>
                                {/* <Text as={'p'} fontWeight='regular' variant='bodyMd'>{warning.message}</Text> */}
                                <ErrorMessage errorText={warning.message} />

                                {/* Show count for variants if available */}
                                {isVariant && warning.count && <Badge>{`${warning.count} ${t("warning(s)")}`}</Badge>}
                            </InlineStack>
                        </InlineStack>
                    ))}
                </BlockStack>
            </BlockStack>
        ));
    }

    const hasParentErrors = !!(warnings?.parent?.warnings && Object.keys(warnings.parent.warnings).length);
    const hasVariantErrors = !!(warnings?.variants && (warnings.variants.warnings || Object.keys(warnings.variants).length));

    return <Modal
        footer={<Box><Text as='p'>
            {t("Can’t find the solution? Contact us through")} {" "}<Link url={`mailto:support@cedcommerce.com?subject=${encodeURIComponent(`${props?.di?.globalState?.get("username") ?? props?.redux?.current?.source?.name ?? "Seller"} has a question about ${process.env.REACT_APP_NAME_AND_ENV ?? "Cedcommerce Amazon Channel"}`)}`} target='_blank'>{t("Email")}</Link>{" "}
        </Text></Box>}
        open={viewProductWarning}
        onClose={() => { setViewProductWarning(false) }}
        title={t('Warnings')}
        size='large' >
        <Modal.Section>
            {loader ? <BlockStack gap={"300"}>
                <SkeletonDisplayText size='small' />
                <SkeletonDisplayText size='medium' maxWidth='50%' />
                <SkeletonDisplayText size='small' />
                <SkeletonBodyText lines={3} />
            </BlockStack> : <BlockStack>
                <FeedActivity data={hasParentErrors && selectedTab === 0 ? processTags.parent : hasVariantErrors && ((hasParentErrors && selectedTab === 1) || !hasParentErrors && selectedTab === 0) ? processTags.child : null} />
                <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange} />
                {hasParentErrors && selectedTab === 0 && renderProductErrorTab()}
                {hasVariantErrors && ((hasParentErrors && selectedTab === 1) || !hasParentErrors && selectedTab === 0) && renderVariantErrorTab()}
            </BlockStack>}
        </Modal.Section>
    </Modal >
}

export default DI(ProductWarningsN);